/**
 * The share of the browser's keepalive quota our events may hold at once.
 *
 * Browsers refuse a keepalive request outright, with the same `TypeError` as a
 * network failure, once the bodies of all in-flight keepalive requests on the
 * page pass 64 KiB. That quota is shared with every other script on the page
 * (Datadog, GTM, the ad pixels), so we claim only half of it.
 */
export const KEEPALIVE_BUDGET_BYTES = 32 * 1024;

let reservedBytes = 0;

/** The size of a request body as sent: UTF-8 bytes, not characters. */
export function byteLength(body: string): number {
    return new Blob([body]).size;
}

/**
 * Reserves room for `body` in the keepalive budget.
 *
 * Returns the function that gives the room back once the request settles, or
 * `null` when the body does not fit: that request goes out without keepalive
 * instead of being refused.
 */
export function reserveKeepalive(body: string): (() => void) | null {
    const bytes = byteLength(body);
    if (reservedBytes + bytes > KEEPALIVE_BUDGET_BYTES) return null;

    reservedBytes += bytes;
    let isReleased = false;

    return () => {
        if (isReleased) return;
        isReleased = true;
        reservedBytes -= bytes;
    };
}

/**
 * Returns whether a task queued now has yet to run.
 */
function watchNextTask(): () => boolean {
    let hasNextTaskRun = false;
    const markRun = () => {
        hasNextTaskRun = true;
    };

    if (typeof MessageChannel === "function") {
        const channel = new MessageChannel();
        channel.port1.onmessage = () => {
            markRun();
            channel.port1.close();
        };
        channel.port2.postMessage(null);
    } else {
        setTimeout(markRun, 0);
    }
    return () => !hasNextTaskRun;
}

/**
 * Sends a request and, when it asked for keepalive and the browser refused it
 * outright, sends it once more without. The quota is shared with every script
 * on the page, so our budget can't rule a refusal out.
 *
 * A refused request never leaves the browser, so the second one doesn't
 * duplicate it. The browser settles a refusal before a task queued just after
 * the request, and a failure after that is not resent. Tasks from different
 * sources have no set order, so a browser could also run that task first: the
 * refusal then counts as a network failure and the event is lost, as it was
 * before this resend existed.
 *
 * WebKit can send a request twice: after a task long enough for a request to
 * reach the server and lose its connection, WebKit settles that failure ahead
 * of every task queued just after the request too, and no other signal tells
 * it from a refusal (a failed request leaves no Resource Timing entry). Use
 * this only for requests a rare exact copy can't harm, such as analytics
 * events; Chromium keeps the two apart.
 *
 * `onRefused` runs before the second request, so a caller can give back the
 * keepalive budget that request no longer uses.
 */
export async function fetchWithKeepaliveFallback<T, I extends RequestInit>(
    send: (init: I) => Promise<T>,
    init: I,
    onRefused?: () => void
): Promise<T> {
    if (!init.keepalive) return send(init);

    // The executor runs `send` now and turns a wrapped fetch that throws, which
    // sent nothing either, into a rejection that settles before the next task.
    const request = new Promise<T>((resolve) => resolve(send(init)));
    const isBeforeNextTask = watchNextTask();
    try {
        return await request;
    } catch (error) {
        if (!isBeforeNextTask()) throw error;
        onRefused?.();
        return send({ ...init, keepalive: false });
    }
}
